import {
  BottomSheet,
  Button,
  Icon,
  IconButton,
  Loader,
  Modal,
  Text,
} from 'components'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'

import { TextField } from 'uikit'
import { useTenant, useToast } from 'shared/hooks'

import { directDebitSchema } from './schemas'
import { DirectDebitForm } from './types'
import { banks } from './constants'

import { usePostPaymentMethod } from 'shared/hooks/services/account'
import { usePutPaymentMethod } from 'shared/hooks/services/account/usePutPaymentMethod'

import { PaymentMethodRequest } from 'services/account'

import { GoBack } from '../Modals'

import styles from './styles.module.scss'

const DirectDebit = () => {
  const { tenant } = useTenant()
  const { accountId } = useParams()

  const location = useLocation()
  const navigate = useNavigate()

  const { addToast } = useToast()

  const {
    setValue,
    watch,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<DirectDebitForm>({
    mode: 'onChange',
    resolver: joiResolver(directDebitSchema),
    defaultValues: {
      paymentMethodType: 'DIRECT_DEBIT',
      accountId,
    },
  })

  const isEditing = location.pathname.endsWith('/edit')

  const { bank } = watch()

  const { mutate: saveMutation, isLoading: saveLoading } =
    usePostPaymentMethod()
  const { mutate: editMutation, isLoading: editLoading } = usePutPaymentMethod()

  const onSubmit = (form: DirectDebitForm) => {
    const request: PaymentMethodRequest = {
      accountNumber: +(
        String(form.account.number) + String(form.account.digit)
      ),
      agencyNumber: form.agency.number,
      ...(form.agency?.digit && {
        agencyNumber: +(String(form.agency.number) + String(form.agency.digit)),
      }),
      bankNumber: +form.bank.number,
      paymentMethodType: form.paymentMethodType,
      accountId: form.accountId,
    }

    const mutate = isEditing ? editMutation : saveMutation

    const successMsg = `Forma de pagamento ${isEditing ? 'editada' : 'cadastrada'} com sucesso.`
    const errorMsg = `Não foi possível ${isEditing ? 'editar' : 'cadastrar'} a forma de pagamento. Tente novamente.`

    mutate(request, {
      onSuccess: () => {
        navigate(-1)
        addToast({ message: successMsg, isSnackBar: true })
      },
      onError: () =>
        addToast({ message: errorMsg, type: 'alert', isSnackBar: true }),
    })
  }

  if (saveLoading || editLoading) return <Loader appearance="screen" />

  return (
    <div className={styles.container}>
      <div>
        <header className={styles.header}>
          <Modal.Root>
            <Modal.Trigger>
              <IconButton className={styles.navigation}>
                <Icon
                  width={20}
                  name="chevron-sm-left"
                  color="element-default"
                />
              </IconButton>
            </Modal.Trigger>

            <GoBack />
          </Modal.Root>
          {/*TODO: remover responsabilidade da tela quando aplicado novo sistema de tenant */}
          <Icon name={`${tenant}-logo`} width={151} height={28} />
        </header>

        <div className={styles.wrapper}>
          <Text weight="semibold" size="3xl" color="element-bold">
            Débito em conta
          </Text>

          <div className={styles.formContent}>
            <div>
              <div className={styles.optionContainer}>
                <BottomSheet.Root>
                  <BottomSheet.Trigger>
                    <div className={styles.selected}>
                      <div>
                        <Text
                          size="sm"
                          weight="medium"
                          color="element-medium"
                          as="label"
                        >
                          Banco
                        </Text>
                        <Text
                          size="md"
                          weight="regular"
                          color="element-default"
                          {...register('bank.name')}
                        >
                          {watch('bank.name') || 'Selecione uma opção'}
                        </Text>
                      </div>

                      <IconButton className={styles.option}>
                        {!!bank?.icon && <Icon width={24} name={bank.icon} />}
                        <Icon width={17} name="edit" color="element-default" />
                        {!bank?.name && <div className={styles.bullet} />}
                      </IconButton>
                    </div>
                  </BottomSheet.Trigger>

                  <BottomSheet.Content>
                    <BottomSheet.Title>Selecione uma opção</BottomSheet.Title>

                    {banks?.map((bank) => (
                      <BottomSheet.Option
                        key={bank.id}
                        onClick={() => {
                          setValue(
                            'bank',
                            {
                              name: bank.name,
                              number: bank.id,
                              icon: bank.icon,
                            },
                            { shouldValidate: true },
                          )
                        }}
                      >
                        <div className={styles.option}>
                          <Icon width={24} name={bank.icon} />
                          <Text size="md">{bank.name}</Text>
                        </div>
                      </BottomSheet.Option>
                    ))}
                  </BottomSheet.Content>
                </BottomSheet.Root>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Text weight="medium" size="lg">
                Dados da conta
              </Text>

              <div className={styles.form}>
                <div className={styles.field}>
                  <TextField
                    {...register('agency.number')}
                    label="Agência"
                    type="number"
                    inputMode="numeric"
                    maxLength={5}
                    message={errors.agency?.number?.message}
                  />
                  <TextField
                    {...register('agency.digit')}
                    label="Dígito "
                    type="number"
                    inputMode="numeric"
                    maxLength={1}
                    message={errors.agency?.digit?.message}
                  />
                </div>

                <div className={styles.field}>
                  <TextField
                    {...register('account.number')}
                    label="Conta"
                    type="number"
                    inputMode="numeric"
                    maxLength={20}
                    message={errors.account?.number?.message}
                  />
                  <TextField
                    {...register('account.digit')}
                    label="Dígito (Opcional)"
                    type="number"
                    inputMode="numeric"
                    maxLength={1}
                    message={errors.account?.digit?.message}
                  />
                </div>
              </div>

              <Button
                className={styles.submit}
                type="submit"
                appearance="primary"
                disabled={!isValid}
              >
                Salvar
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DirectDebit
